import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Form, Alert, Spinner, Row, Col } from 'react-bootstrap';
import UpdateLayout from '../../layouts/UpdateLayout';
import AppScriptAPI from '../../services/appscriptAPI';
import { useUpdatePermissions } from '../../hooks/useUpdatePermissions';
import { useConsentModal } from '../../hooks/useConsentModal';
import AppToast from '../../components/AppToast';

function UpdateContact() {
  const { recordId } = useParams();
  const [searchParams] = useSearchParams();
  const sheetId = searchParams.get('sheetId');
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  const { canEdit } = useUpdatePermissions();
  const { confirmWithConsent, ConsentModalJSX } = useConsentModal();

  const [recordData, setRecordData] = useState(null);
  const [originalData, setOriginalData] = useState(null);

  const [formData, setFormData] = useState({
    uniqueId: '',
    name: '',
    address: '',
    phoneHome: '',
    mobile: '',
    email: '',
  });

  useEffect(() => {
    if (!recordId || !sheetId) {
      setAlert({
        show: true,
        message:
          'Missing required parameters. Please search for the record again.',
        type: 'danger',
      });
      setLoading(false);
      return;
    }
    loadRecordData();
  }, [recordId, sheetId]);

  const normalizePhone = (val) => {
    const s = String(val || '').trim();
    return /^\d{9}$/.test(s) ? '0' + s : s;
  };

  const loadRecordData = async () => {
    try {
      const response = await AppScriptAPI.getContactDetails(recordId, sheetId);

      if (!response.success) {
        setAlert({
          show: true,
          message: response.message || 'Failed to load contact details.',
          type: 'danger',
        });
        setLoading(false);
        return;
      }

      setRecordData(response.record);

      const data = {
        uniqueId: response.record.uniqueId || '',
        name: response.record.name || '',
        address: response.record.address || '',
        phoneHome: normalizePhone(response.record.phoneHome),
        mobile: normalizePhone(response.record.mobile),
        email: response.record.email || '',
      };

      setFormData(data);
      setOriginalData(JSON.parse(JSON.stringify(data)));
      setLoading(false);
    } catch (error) {
      setAlert({
        show: true,
        message: 'An error occurred: ' + error.message,
        type: 'danger',
      });
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const hasChanges = () => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  };

  const validatePhone = (phone) => {
    if (!phone) return { valid: true };

    const cleanPhone = phone.replace(/[\s\-]/g, '');
    const landlinePattern = /^0[1-9]\d{8}$/;
    const mobilePattern = /^07[0-9]\d{7}$/;
    const internationalPattern = /^\+94[1-9]\d{8}$/;

    if (
      landlinePattern.test(cleanPhone) ||
      mobilePattern.test(cleanPhone) ||
      internationalPattern.test(cleanPhone)
    ) {
      return { valid: true };
    }

    return {
      valid: false,
      message:
        'Invalid phone number format. Use format: 0112345678 or 0771234567',
    };
  };

  const validateEmail = (email) => {
    if (!email) return { valid: true };

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      return {
        valid: false,
        message: 'Invalid email format.',
      };
    }
    return { valid: true };
  };

  const validateAddress = (address) => {
    if (!address) return { valid: true };

    const trimmedAddress = address.trim();
    if (trimmedAddress.length > 500) {
      return {
        valid: false,
        message: 'Address is too long (max 500 characters).',
      };
    }
    return { valid: true };
  };

  const handleSave = async () => {
    // Validate
    const addressValidation = validateAddress(formData.address);
    if (!addressValidation.valid) {
      setAlert({
        show: true,
        message: addressValidation.message,
        type: 'danger',
      });
      return;
    }

    const phoneHomeValidation = validatePhone(formData.phoneHome);
    if (!phoneHomeValidation.valid) {
      setAlert({
        show: true,
        message: 'Home Phone: ' + phoneHomeValidation.message,
        type: 'danger',
      });
      return;
    }

    const mobileValidation = validatePhone(formData.mobile);
    if (!mobileValidation.valid) {
      setAlert({
        show: true,
        message: 'Mobile: ' + mobileValidation.message,
        type: 'danger',
      });
      return;
    }

    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.valid) {
      setAlert({
        show: true,
        message: emailValidation.message,
        type: 'danger',
      });
      return;
    }

    if (!hasChanges()) {
      setAlert({
        show: true,
        message: 'No changes detected.',
        type: 'warning',
      });
      return;
    }

    if (!(await confirmWithConsent())) {
      return;
    }

    setSaving(true);

    try {
      const updateData = {
        uniqueId: recordData.uniqueId,
        address: formData.address.trim(),
        phoneHome: formData.phoneHome.trim(),
        mobile: formData.mobile.trim(),
        email: formData.email.trim(),
        sheetId: recordData.sheetId,
        rowIndex: recordData.rowIndex,
      };

      const response = await AppScriptAPI.updateContactDetails(updateData);

      if (response.success) {
        setAlert({
          show: true,
          message: 'Contact details updated successfully.',
          type: 'success',
        });
        setOriginalData(JSON.parse(JSON.stringify(formData)));
      } else {
        setAlert({
          show: true,
          message:
            'Failed to update: ' + (response.message || 'Unknown error'),
          type: 'danger',
        });
      }
    } catch (error) {
      setAlert({
        show: true,
        message: 'An error occurred: ' + error.message,
        type: 'danger',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    navigate(`/update/personal/${recordId}?sheetId=${sheetId}`);
  };

  const handlePrevious = () => {
    navigate(`/update/basic/${recordId}?sheetId=${sheetId}`);
  };

  if (loading) {
    return (
      <UpdateLayout
        currentStep={2}
        title="Update Contact Details"
        recordId={recordId}
        sheetId={sheetId}
      >
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading contact details...</p>
        </div>
      </UpdateLayout>
    );
  }

  if (!recordData) {
    return (
      <UpdateLayout
        currentStep={2}
        title="Update Contact Details"
        recordId={recordId}
        sheetId={sheetId}
      >
        <Alert variant="danger">
          <strong>Error!</strong>
          <br />
          {alert.message || 'Failed to load contact details.'}
        </Alert>
      </UpdateLayout>
    );
  }

  return (
    <UpdateLayout
      currentStep={2}
      title="Update Contact Details"
      recordId={recordId}
      sheetId={sheetId}
      onSave={handleSave}
      onNext={handleNext}
      onPrevious={handlePrevious}
      isSaving={saving}
      hasChanges={hasChanges()}
      canEdit={canEdit}
    >
      {ConsentModalJSX}
      <AppToast
        show={alert.show}
        type={alert.type}
        message={alert.message}
        onClose={() => setAlert(a => ({ ...a, show: false }))}
      />

      <Form>
        <div className="section-header">Record Information (Read Only)</div>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Unique ID</Form.Label>
              <Form.Control
                type="text"
                value={formData.uniqueId}
                className="readonly-field"
                readOnly
              />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                className="readonly-field"
                readOnly
              />
            </Form.Group>
          </Col>
        </Row>

        <div className="section-header">Contact Details (Editable)</div>

        <Form.Group className="mb-3">
          <Form.Label>Address</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            name="address"
            value={formData.address}
            onChange={handleInputChange}
            placeholder="Enter full address"
            maxLength="500"
            disabled={!canEdit}
          />
          <Form.Text>Enter the complete residential address.</Form.Text>
        </Form.Group>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Home Phone Number</Form.Label>
              <Form.Control
                type="tel"
                name="phoneHome"
                value={formData.phoneHome}
                onChange={handleInputChange}
                placeholder="0112345678"
                maxLength="15"
                disabled={!canEdit}
              />
              <Form.Text>Landline number (optional).</Form.Text>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Mobile Number</Form.Label>
              <Form.Control
                type="tel"
                name="mobile"
                value={formData.mobile}
                onChange={handleInputChange}
                placeholder="0771234567"
                maxLength="15"
                disabled={!canEdit}
              />
              <Form.Text>Mobile contact number (optional).</Form.Text>
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-3">
          <Form.Label>Email Address</Form.Label>
          <Form.Control
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="example@email.com"
            maxLength="100"
            disabled={!canEdit}
          />
          <Form.Text>Email address (optional).</Form.Text>
        </Form.Group>
      </Form>
    </UpdateLayout>
  );
}

export default UpdateContact;
